<!-- src/components/forms/completed/WorksiteInspectionCompleted.vue -->
<template>
  <div class="space-y-16 animate-fadeIn text-left">
    
    <!-- 1. Report Metadata -->
    <div class="flex flex-wrap items-center justify-between gap-6 p-8 bg-slate-900 rounded-[40px] text-white shadow-2xl relative overflow-hidden">
      <div class="absolute top-0 right-0 p-10 opacity-5">
        <ShieldCheckIcon class="h-32 w-32" />
      </div>
      <div class="relative z-10 space-y-2">
        <p class="text-[10px] font-black uppercase tracking-[0.2em] text-[#20FCF1]">Quality Assurance Record</p>
        <h2 class="text-2xl font-black uppercase tracking-tight">{{ submissionData.project }}</h2>
        <div class="flex items-center gap-4 text-slate-400 text-xs font-bold">
          <span>JOB: {{ submissionData.jobNumber }}</span>
          <span class="w-1.5 h-1.5 rounded-full bg-slate-700"></span>
          <span>REF: {{ submissionData.humanReadableId || submissionData.referenceNumber }}</span>
        </div>
      </div>
      <div class="relative z-10 flex flex-col items-end gap-2">
        <span class="bg-[#20FCF1] text-[#0A1630] px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg">Inspection Complete</span>
        <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{{ formatDate(submissionData.submittedAt) }}</p>
      </div>
    </div>

    <!-- 1. Site Parameters -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <IdentificationIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">1. Site Parameters</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
        <div class="StatCard bg-white">
          <span class="label">Inspected By</span>
          <span class="value">{{ submissionData.inspectedBy }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Inspection Purpose</span>
          <span class="value">{{ submissionData.inspectionPurpose === 'Other' ? submissionData.inspectionPurposeOther : formatText(submissionData.inspectionPurpose) }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Client Entity</span>
          <span class="value">{{ submissionData.client || 'N/A' }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Shift Period</span>
          <span class="value text-[11px]">{{ formatDate(submissionData.shiftStartTime) }} - {{ formatDate(submissionData.shiftEndTime) }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">COSS in Charge</span>
          <span class="value uppercase">{{ submissionData.nameOfCOSS || 'N/A' }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Protection Method</span>
          <span class="value">{{ submissionData.typeOfProtection === 'Other' ? submissionData.typeOfProtectionOther : formatText(submissionData.typeOfProtection) }}</span>
        </div>
      </div>

      <!-- Planned Works & Timeline (Part of Site/Work Parameters) -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6">
        <div class="p-8 bg-white border border-slate-200 rounded-[32px] shadow-sm">
          <div class="flex items-center gap-2 mb-4">
            <ClipboardDocumentIcon class="h-5 w-5 text-[#20FCF1]" />
            <h4 class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Planned Works vs Actual</h4>
          </div>
          <p class="text-sm text-slate-600 leading-relaxed italic mb-6">"{{ submissionData.plannedWorks || 'No details provided.' }}"</p>
          <div class="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
            <span class="text-[10px] font-black uppercase text-slate-400">Status:</span>
            <span :class="submissionData.plannedWorksAchieved ? 'text-green-600' : 'text-red-600'" class="text-xs font-black uppercase tracking-widest">
              {{ submissionData.plannedWorksAchieved ? 'Works Achieved' : 'Works Not Fully Achieved' }}
            </span>
          </div>
        </div>

        <div class="p-8 bg-white border border-slate-200 rounded-[32px] shadow-sm">
          <div class="flex items-center gap-2 mb-4">
            <ClockIcon class="h-5 w-5 text-[#20FCF1]" />
            <h4 class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Timeline of Events</h4>
          </div>
          <p class="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{{ submissionData.timelineOfEvents || 'No timeline recorded.' }}</p>
        </div>
      </div>
    </section>

    <!-- 2. Safety Inspections -->
    <section class="space-y-6">
      <div class="flex items-center gap-4 mb-6">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <ShieldCheckIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">2. Safety Inspections</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <template v-for="section in inspectionCategories.safety" :key="section.name">
          <div v-for="q in section.questions" :key="q.id" 
               class="flex flex-col p-6 bg-white border border-slate-100 rounded-3xl shadow-sm hover:border-[#20FCF1]/30 transition-all">
            <div class="flex items-start justify-between gap-4 mb-3">
              <p class="text-xs font-bold text-slate-700 leading-relaxed">{{ q.text }}</p>
              <span :class="getStatusClass(submissionData[q.modelKey])" class="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex-shrink-0">
                {{ formatYesNoNA(submissionData[q.modelKey]) }}
              </span>
            </div>
            <p v-if="submissionData[q.detailsModelKey]" class="text-[11px] text-rose-500 font-medium bg-rose-50 p-3 rounded-xl border border-rose-100">
              <strong>Findings:</strong> {{ submissionData[q.detailsModelKey] }}
            </p>
          </div>
        </template>
      </div>

      <div v-if="submissionData.safetyComments" class="bg-slate-50 p-5 rounded-2xl border border-slate-100">
         <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Section 2 (Safety) Comments & Good Practices</p>
         <p class="text-xs font-medium text-slate-700 italic">"{{ submissionData.safetyComments }}"</p>
      </div>
    </section>

    <!-- 3. Health Inspections -->
    <section class="space-y-6">
      <div class="flex items-center gap-4 mb-6">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <HeartIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">3. Health Inspections</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <template v-for="section in inspectionCategories.health" :key="section.name">
          <div v-for="q in section.questions" :key="q.id" 
               class="flex flex-col p-6 bg-white border border-slate-100 rounded-3xl shadow-sm hover:border-[#20FCF1]/30 transition-all">
            <div class="flex items-start justify-between gap-4 mb-3">
              <p class="text-xs font-bold text-slate-700 leading-relaxed">{{ q.text }}</p>
              <span :class="getStatusClass(submissionData[q.modelKey])" class="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex-shrink-0">
                {{ formatYesNoNA(submissionData[q.modelKey]) }}
              </span>
            </div>
            <p v-if="submissionData[q.detailsModelKey]" class="text-[11px] text-rose-500 font-medium bg-rose-50 p-3 rounded-xl border border-rose-100">
              <strong>Findings:</strong> {{ submissionData[q.detailsModelKey] }}
            </p>
          </div>
        </template>
      </div>

      <div v-if="submissionData.healthComments" class="bg-slate-50 p-5 rounded-2xl border border-slate-100">
         <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Section 3 (Health) Comments & Good Practices</p>
         <p class="text-xs font-medium text-slate-700 italic">"{{ submissionData.healthComments }}"</p>
      </div>
    </section>

    <!-- 4. Environmental Inspections -->
    <section class="space-y-6">
      <div class="flex items-center gap-4 mb-6">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <GlobeEuropeAfricaIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">4. Environmental Inspections</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <template v-for="section in inspectionCategories.environmental" :key="section.name">
          <div v-for="q in section.questions" :key="q.id" 
               class="flex flex-col p-6 bg-white border border-slate-100 rounded-3xl shadow-sm hover:border-[#20FCF1]/30 transition-all">
            <div class="flex items-start justify-between gap-4 mb-3">
              <p class="text-xs font-bold text-slate-700 leading-relaxed">{{ q.text }}</p>
              <span :class="getStatusClass(submissionData[q.modelKey])" class="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex-shrink-0">
                {{ formatYesNoNA(submissionData[q.modelKey]) }}
              </span>
            </div>
            <p v-if="submissionData[q.detailsModelKey]" class="text-[11px] text-rose-500 font-medium bg-rose-50 p-3 rounded-xl border border-rose-100">
              <strong>Findings:</strong> {{ submissionData[q.detailsModelKey] }}
            </p>
          </div>
        </template>
      </div>

      <div v-if="submissionData.environmentalComments" class="bg-slate-50 p-5 rounded-2xl border border-slate-100">
         <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Section 4 (Environmental) Comments & Good Practices</p>
         <p class="text-xs font-medium text-slate-700 italic">"{{ submissionData.environmentalComments }}"</p>
      </div>
    </section>

    <!-- 5. Safe System of Work -->
    <section class="space-y-6">
      <div class="flex items-center gap-4 mb-6">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <CheckBadgeIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">5. Safe System of Work</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <template v-for="section in inspectionCategories.ssow" :key="section.name">
          <div v-for="q in section.questions" :key="q.id" 
               class="flex flex-col p-6 bg-white border border-slate-100 rounded-3xl shadow-sm hover:border-[#20FCF1]/30 transition-all">
            <div class="flex items-start justify-between gap-4 mb-3">
              <p class="text-xs font-bold text-slate-700 leading-relaxed">{{ q.text }}</p>
              <span :class="getStatusClass(submissionData[q.modelKey])" class="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex-shrink-0">
                {{ formatYesNoNA(submissionData[q.modelKey]) }}
              </span>
            </div>
            <p v-if="submissionData[q.detailsModelKey]" class="text-[11px] text-rose-500 font-medium bg-rose-50 p-3 rounded-xl border border-rose-100">
              <strong>Findings:</strong> {{ submissionData[q.detailsModelKey] }}
            </p>
          </div>
        </template>
      </div>

      <div v-if="submissionData.ssowComments" class="bg-slate-50 p-5 rounded-2xl border border-slate-100">
         <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Section 5 (SSoW) Comments & Good Practices</p>
         <p class="text-xs font-medium text-slate-700 italic">"{{ submissionData.ssowComments }}"</p>
      </div>
    </section>

    <!-- 6. Non-Conformance & Issues -->
    <section class="bg-[#0A1630] p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden">
      <div class="absolute bottom-0 right-0 p-10 opacity-5">
        <ExclamationTriangleIcon class="h-40 w-40" />
      </div>
      <div class="relative z-10 space-y-8">
        <div class="flex items-center gap-4 mb-6">
          <div class="bg-red-500 text-white w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-red-400/30">
            <ExclamationCircleIcon class="h-6 w-6 text-white" />
          </div>
          <h3 class="text-2xl font-black text-white uppercase tracking-tight">6. Non-Conformance & Issues</h3>
        </div>

        <div v-if="hasIssues" class="grid grid-cols-1 gap-4">
          <template v-for="idx in 5" :key="idx">
            <div v-if="submissionData['issue' + idx + '_text']" class="bg-white/5 border border-white/10 p-6 rounded-[24px] grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
              <div class="md:col-span-2">
                <span class="text-[9px] font-black uppercase text-[#20FCF1] tracking-widest block mb-1">Issue #{{ idx }}</span>
                <p class="text-sm font-medium text-slate-200">{{ submissionData['issue' + idx + '_text'] }}</p>
              </div>
              <div>
                <span class="text-[9px] font-black uppercase text-slate-500 tracking-widest block mb-1">Responsibility</span>
                <p class="text-xs font-bold">{{ submissionData['issue' + idx + '_person'] }}</p>
              </div>
              <div class="flex gap-2 justify-end">
                <span :class="statusClass(submissionData['issue' + idx + '_completed'])" class="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-tighter">Done: {{ formatYesNoNA(submissionData['issue' + idx + '_completed']) }}</span>
                <span :class="statusClass(submissionData['issue' + idx + '_followUp'])" class="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-tighter">FollowUp: {{ formatYesNoNA(submissionData['issue' + idx + '_followUp']) }}</span>
              </div>
            </div>
          </template>
        </div>
        <div v-else class="p-6 bg-white/5 rounded-2xl border border-white/10 text-center">
          <p class="text-[10px] font-black uppercase tracking-widest text-slate-400">Zero non-conformance issues recorded during inspection</p>
        </div>
      </div>
    </section>

    <!-- 7. Site Evidence Attachments -->
    <section>
      <div class="flex items-center gap-4 mb-6">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <CameraIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">7. Site Evidence Attachments</h3>
      </div>
      
      <div v-if="submissionData.imageUrls?.length || submissionData.documentUrls?.length" class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div v-if="submissionData.imageUrls?.length" class="p-6 bg-slate-50 rounded-[32px] border border-slate-100">
          <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-4">Site Photos</p>
          <div class="grid grid-cols-2 gap-4">
            <div v-for="(url, idx) in submissionData.imageUrls" :key="idx" class="group relative aspect-square overflow-hidden rounded-2xl border-4 border-white shadow-md">
              <img :src="url" class="h-full w-full object-cover" />
              <a :href="url" target="_blank" class="absolute inset-0 bg-[#0A1630]/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                <MagnifyingGlassIcon class="h-6 w-6 text-white" />
              </a>
            </div>
          </div>
        </div>

        <div v-if="submissionData.documentUrls?.length" class="p-6 bg-slate-50 rounded-[32px] border border-slate-100">
          <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-4">Support Documentation</p>
          <div class="space-y-2">
            <a v-for="(url, idx) in submissionData.documentUrls" :key="idx" :href="url" target="_blank" 
               class="flex items-center p-4 bg-white border border-slate-200 rounded-2xl hover:border-[#20FCF1] transition-all group shadow-sm">
               <component :is="getFileIcon(url)" class="h-5 w-5 text-slate-400 group-hover:text-[#FD5E00] mr-4" />
               <span class="text-[10px] font-black text-[#0A1630] truncate flex-1 uppercase tracking-tight">{{ getFileNameFromUrl(url) }}</span>
            </a>
          </div>
        </div>
      </div>
      <div v-else class="p-8 bg-slate-50 rounded-[32px] border-2 border-dashed border-slate-200 text-center">
        <p class="text-[10px] font-black uppercase tracking-widest text-slate-400">No photographic or supporting evidence attached</p>
      </div>
    </section>

    <!-- 8. Official Inspection Sign-Off (Stage 1 Attestation) -->
    <section class="border-t pt-12">
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <CheckCircleIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">8. Official Inspection Sign-Off</h3>
      </div>

      <div class="flex flex-col md:flex-row justify-between items-end gap-12">
        <div class="space-y-6 max-w-lg text-left">
          <div class="p-8 bg-slate-900 rounded-[32px] border border-slate-800 text-slate-300 shadow-lg">
            <p class="text-[10px] font-black text-[#20FCF1] uppercase tracking-widest mb-2">Inspector Declaration</p>
            <p class="text-xs font-medium italic leading-relaxed">
              "I formally declare that this worksite inspection has been conducted according to company safety protocols and the findings recorded are true."
            </p>
          </div>
        </div>

        <div v-if="submissionData.signatureDataUrl" class="bg-white p-8 rounded-[40px] border border-slate-200 shadow-2xl flex flex-col items-center min-w-[280px]">
          <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-6">Certified Inspector Seal</p>
          <img :src="submissionData.signatureDataUrl" alt="Assessor Seal" class="max-h-24 object-contain mb-6" />
          <div class="w-full h-px bg-slate-100 mb-6"></div>
          <div class="flex items-center gap-2">
            <CheckBadgeIcon class="h-5 w-5 text-[#20FCF1]" />
            <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-[0.1em]">Verification Passed</span>
          </div>
          <p class="text-[10px] font-bold text-slate-400 mt-3">{{ formatDate(submissionData.signatureDate || submissionData.submittedAt) }}</p>
        </div>
      </div>
    </section>

    <!-- 9. Management Review (Stage 2 - Interactive) -->
    <section id="management-appraisal" class="pt-16 border-t-8 border-slate-100 animate-fadeIn text-left">
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <ShieldCheckIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">9. Management Review</h2>
      </div>

      <div class="bg-[#0A1630] p-8 md:p-12 rounded-[50px] text-white shadow-2xl relative overflow-hidden border-b-[12px] border-[#20FCF1]">
        <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
          <ShieldCheckIcon class="h-64 w-64 text-white" />
        </div>
        
        <div class="relative z-10">
          <div class="flex flex-col md:flex-row items-start md:items-center justify-between mb-10 gap-6 text-left">
            <div>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.4em] mb-3">Stage 2 - Internal Compliance</p>
              <h2 class="text-3xl font-black uppercase tracking-tight leading-none text-left">Senior Management Review</h2>
            </div>
            <div v-if="appraisalData.status" :class="statusClass(appraisalData.status)" class="px-6 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl border border-white/10 backdrop-blur-md">
              {{ appraisalData.status }}
            </div>
          </div>

          <div class="space-y-6">
            <!-- Appraisal Notes -->
            <div>
               <label class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] ml-2 block mb-3">Management Corrective & Preventive Actions (Mandatory)<span class="text-red-500">*</span></label>
               <textarea 
                 v-model="appraisalData.notes" 
                 :readonly="!hasAppraisalAccess" 
                 rows="5" 
                 class="w-full bg-white/5 border border-white/10 rounded-[24px] p-6 text-sm font-medium text-slate-200 focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all shadow-inner placeholder:text-white/20" 
                 placeholder="Enter managerial findings, request for information, or final close-out directives..."
               ></textarea>
            </div>

            <!-- Optional Lessons Learned & CAPA Fields -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                 <label class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] ml-2 block mb-3">Lessons Learned (Optional)</label>
                 <textarea 
                   v-model="appraisalData.lessonsLearned" 
                   :readonly="!hasAppraisalAccess" 
                   rows="4" 
                   class="w-full bg-white/5 border border-white/10 rounded-[24px] p-6 text-sm font-medium text-slate-200 focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all shadow-inner placeholder:text-white/20" 
                   placeholder="Improvements and safety insights identified..."
                 ></textarea>
              </div>
              <div>
                 <label class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] ml-2 block mb-3">Any Additional Comments (Optional)</label>
                 <textarea 
                   v-model="appraisalData.recommendations" 
                   :readonly="!hasAppraisalAccess" 
                   rows="4" 
                   class="w-full bg-white/5 border border-white/10 rounded-[24px] p-6 text-sm font-medium text-slate-200 focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all shadow-inner placeholder:text-white/20" 
                   placeholder="Specific, measurable actions..."
                 ></textarea>
              </div>
            </div>

            <!-- AUDIT SEAL -->
            <div v-if="appraisalData.appraisedBy" class="mt-8 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6 animate-fadeIn text-left">
               <div class="flex items-center gap-6">
                  <div class="h-16 w-16 bg-white p-4 rounded-2xl flex items-center justify-center shadow-lg"><CheckCircleIcon class="h-8 w-8 text-[#20FCF1]" /></div>
                  <div>
                     <p class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mb-1">Authoritative Close-Out Seal</p>
                     <p class="text-xl font-black text-white uppercase tracking-tight leading-none text-left">{{ appraisalData.appraisedBy }}</p>
                     <p class="text-[9px] font-bold text-slate-500 uppercase mt-2 tracking-widest">Handshake Verified: {{ formatDate(appraisalData.appraisedAt) }}</p>
                  </div>
               </div>
               
               <div v-if="hasAppraisalAccess" class="flex flex-wrap justify-center gap-2">
                 <button type="button" v-for="status in ['In Progress', 'Requires Action', 'Closed/Resolved']" :key="status" @click="appraisalData.status = status" :class="appraisalData.status === status ? 'bg-[#20FCF1] text-[#0A1630] scale-105' : 'bg-white/5 text-white'" class="px-5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all">{{ status }}</button>
               </div>
            </div>

            <div v-if="hasAppraisalAccess" class="mt-8 flex justify-end">
               <button 
                 type="button"
                 @click="saveAppraisal" 
                 :disabled="isSavingAppraisal" 
                 class="bg-[#20FCF1] text-[#0A1630] px-12 py-5 rounded-[24px] font-black uppercase tracking-[0.2em] text-xs shadow-2xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
               >
                 <span v-if="isSavingAppraisal" class="flex items-center gap-2">
                   <div class="animate-spin h-4 w-4 border-2 border-[#0A1630] border-t-transparent rounded-full"></div>
                   Syncing...
                 </span>
                 <span v-else>Apply Management Seal</span>
               </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
/* eslint-disable vue/no-mutating-props */
import { ref, reactive, onMounted, computed } from 'vue';
import { db } from '@/firebaseConfig';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';

import { 
  IdentificationIcon, ShieldCheckIcon, ClipboardDocumentIcon, 
  ClockIcon, ExclamationTriangleIcon, ExclamationCircleIcon, CameraIcon, 
  MagnifyingGlassIcon, CheckCircleIcon, CheckBadgeIcon,
  HeartIcon, GlobeEuropeAfricaIcon
} from '@heroicons/vue/24/outline';

const props = defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true },
  formatText: { type: Function, required: true },
  formatYesNoNA: { type: Function, required: true },
  getStatusClass: { type: Function, required: true },
  getFileIcon: { type: Function, required: true },
  getFileNameFromUrl: { type: Function, required: true },
  isImage: { type: Function, required: false }
});

const userStore = useUserStore();
const isSavingAppraisal = ref(false);

const appraisalData = reactive({
  notes: '',
  status: 'Pending Review',
  lessonsLearned: '',
  recommendations: '',
  appraisedBy: '',
  appraisedAt: null
});

const hasAppraisalAccess = computed(() => true);

const statusClass = (status) => {
  const s = String(status || '').toUpperCase();
  if (s.includes('CLOSED') || s.includes('YES') || s.includes('TRUE')) return 'bg-emerald-100 text-emerald-800';
  if (s.includes('ACTION') || s.includes('NO') || s.includes('FALSE')) return 'bg-rose-500 text-white';
  return 'bg-amber-500 text-[#0A1630]';
};

const saveAppraisal = async () => {
    if (!hasAppraisalAccess.value || isSavingAppraisal.value) return;
    isSavingAppraisal.value = true;
    try {
        let targetId = props.submissionData.id || 
                       props.submissionData.docId || 
                       props.submissionData._id || 
                       props.submissionData.uid ||
                       props.submissionData.idField;

        if (!targetId && props.submissionData.humanReadableId) {
            const q = query(
                collection(db, 'healthsafetyFormSubmissions'), 
                where('humanReadableId', '==', props.submissionData.humanReadableId)
            );
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                targetId = querySnapshot.docs[0].id;
            }
        }

        if (!targetId) {
            throw new Error(`Firestore document reference is missing.`);
        }

        const docRef = doc(db, 'healthsafetyFormSubmissions', targetId);
        const adminName = `${userStore.userProfile?.firstName || 'System'} ${userStore.userProfile?.lastName || 'Admin'}`;
        const timestamp = new Date().toISOString();
        
        const updateObj = {
            appraisalStage2: {
                notes: appraisalData.notes,
                status: appraisalData.status,
                lessonsLearned: appraisalData.lessonsLearned,
                recommendations: appraisalData.recommendations,
                appraisedBy: adminName,
                appraisedAt: timestamp
            },
            status: appraisalData.status 
        };

        await updateDoc(docRef, updateObj);
        
        if (!props.submissionData.appraisalStage2) {
            props.submissionData.appraisalStage2 = {};
        }
        Object.assign(props.submissionData.appraisalStage2, updateObj.appraisalStage2);
        props.submissionData.status = appraisalData.status;

        appraisalData.appraisedBy = adminName;
        appraisalData.appraisedAt = timestamp;
        
        alert("Management Appraisal Registry Synchronised.");
    } catch (e) {
        console.error("Firestore Update Error:", e);
        alert("Registry sync failure: " + e.message);
    } finally {
        isSavingAppraisal.value = false;
    }
};

onMounted(() => {
    if (props.submissionData?.appraisalStage2) {
        Object.assign(appraisalData, props.submissionData.appraisalStage2);
    }
});

const hasIssues = computed(() => {
  const s = props.submissionData;
  return !!(s.issue1_text || s.issue2_text || s.issue3_text || s.issue4_text || s.issue5_text);
});

const inspectionCategories = {
  safety: [
    { name: 'Workplace Safety', questions: [ { id: 'ws_accessEgress', text: 'Access/Egress – Is it safe? Think: slips/trips, lighting, underfoot conditions, etc.', modelKey: 'safety_ws_accessEgress', detailsModelKey: 'safety_ws_accessEgress_details' } ] },
    { name: 'PPE (Generic)', questions: [ 
        { id: 'ppe_suitable', text: 'Suitable for the planned works?', modelKey: 'safety_ppe_suitable', detailsModelKey: 'safety_ppe_suitable_details' }, 
        { id: 'ppe_goodCondition', text: 'PPE in good condition and clean?', modelKey: 'safety_ppe_goodCondition', detailsModelKey: 'safety_ppe_goodCondition_details' }, 
        { id: 'ppe_fitsCorrectly', text: 'Does the PPE fit correctly?', modelKey: 'safety_ppe_fitsCorrectly', detailsModelKey: 'safety_ppe_fitsCorrectly_details' }, 
        { id: 'ppe_expirationDate', text: 'Check PPE expiration date', modelKey: 'safety_ppe_expirationDate', detailsModelKey: 'safety_ppe_expirationDate_details' }, 
        { id: 'ppe_headProtection', text: 'Head Protection good?', modelKey: 'safety_ppe_headProtection', detailsModelKey: 'safety_ppe_headProtection_details' }, 
        { id: 'ppe_bodyProtection', text: 'Body Protection good?', modelKey: 'safety_ppe_bodyProtection', detailsModelKey: 'safety_ppe_bodyProtection_details' }, 
        { id: 'ppe_handProtection', text: 'Hand Protection good?', modelKey: 'safety_ppe_handProtection', detailsModelKey: 'safety_ppe_handProtection_details' }, 
        { id: 'ppe_footProtection', text: 'Foot Protection good?', modelKey: 'safety_ppe_footProtection', detailsModelKey: 'safety_ppe_footProtection_details' } 
    ] },
    { name: 'PPE (Task Specific)', questions: [ 
        { id: 'ppeTask_eyeFace', text: 'Eye/Face Protection good?', modelKey: 'safety_ppeTask_eyeFace', detailsModelKey: 'safety_ppeTask_eyeFace_details' }, 
        { id: 'ppeTask_respiratory', text: 'Respiratory Protection good?', modelKey: 'safety_ppeTask_respiratory', detailsModelKey: 'safety_ppeTask_respiratory_details' }, 
        { id: 'ppeTask_hearing', text: 'Hearing Protection good?', modelKey: 'safety_ppeTask_hearing', detailsModelKey: 'safety_ppeTask_hearing_details' } 
    ] },
    { name: 'Emergency Evacuation Plan', questions: [ 
        { id: 'evac_firstAid', text: 'First Aid Kits in date?', modelKey: 'safety_evac_firstAid', detailsModelKey: 'safety_evac_firstAid_details' }, 
        { id: 'evac_fireExtinguisher', text: 'Fire Extinguisher on-site?', modelKey: 'safety_evac_fireExtinguisher', detailsModelKey: 'safety_evac_fireExtinguisher_details' }, 
        { id: 'evac_spillKits', text: 'Emergency Spill Kits available?', modelKey: 'safety_evac_spillKits', detailsModelKey: 'safety_evac_spillKits_details' }, 
        { id: 'evac_exitProcedure', text: 'Exit Procedure well briefed?', modelKey: 'safety_evac_exitProcedure', detailsModelKey: 'safety_evac_exitProcedure_details' } 
    ] }
  ],
  health: [
    { name: 'Physical Hazards', questions: [ 
        { id: 'ph_noise', text: 'Noise level measures?', modelKey: 'health_ph_noise', detailsModelKey: 'health_ph_noise_details' }, 
        { id: 'ph_havs', text: 'Any Hand-Arm Vibration Syndrome (HAVS) activities?', modelKey: 'health_ph_havs', detailsModelKey: 'health_ph_havs_details' }, 
        { id: 'ph_extremeConditions', text: 'Extreme Site Conditions?', modelKey: 'health_ph_extremeConditions', detailsModelKey: 'health_ph_extremeConditions_details' }, 
        { id: 'ph_ergonomics', text: 'Poor Ergonomics?', modelKey: 'health_ph_ergonomics', detailsModelKey: 'health_ph_ergonomics_details' }, 
        { id: 'ph_repetitiveMotion', text: 'Repetitive Motion?', modelKey: 'health_ph_repetitiveMotion', detailsModelKey: 'health_ph_repetitiveMotion_details' }, 
        { id: 'ph_manualHandling', text: 'Manual Handling good?', modelKey: 'health_ph_manualHandling', detailsModelKey: 'health_ph_manualHandling_details' } 
    ] },
    { name: 'Air Quality Monitoring', questions: [ { id: 'aqm_dustFumes', text: 'Dust & Fumes managed?', modelKey: 'health_aqm_dustFumes', detailsModelKey: 'health_aqm_dustFumes_details' } ] },
    { name: 'Biological Hazard', questions: [ { id: 'bh_infectiousDiseases', text: 'Infectious Diseases control?', modelKey: 'health_bh_infectiousDiseases', detailsModelKey: 'health_bh_infectiousDiseases_details' } ] },
    { name: 'Psychosocial Hazard', questions: [ 
        { id: 'psh_mentalHealth', text: 'Mental Health/Stress managed?', modelKey: 'health_psh_mentalHealth', detailsModelKey: 'health_psh_mentalHealth_details' }, 
        { id: 'psh_workplaceViolence', text: 'Workplace Violence managed?', modelKey: 'health_psh_workplaceViolence', detailsModelKey: 'health_psh_workplaceViolence_details' } 
    ] },
    { name: 'Mechanical Hazard', questions: [ { id: 'mh_peoplePlantInterface', text: 'People and Plant Interface?', modelKey: 'health_mh_peoplePlantInterface', detailsModelKey: 'health_mh_peoplePlantInterface_details' } ] }
  ],
  environmental: [
    { name: 'Pollution Control Measures', questions: [ 
        { id: 'pcm_storageMaterials', text: 'Storage of Materials good?', modelKey: 'env_pcm_storageMaterials', detailsModelKey: 'env_pcm_storageMaterials_details' }, 
        { id: 'pcm_handlingMaterials', text: 'Handling of Materials good?', modelKey: 'env_pcm_handlingMaterials', detailsModelKey: 'env_pcm_handlingMaterials_details' }, 
        { id: 'pcm_wasteSegregated', text: 'Waste Segregated?', modelKey: 'env_pcm_wasteSegregated', detailsModelKey: 'env_pcm_wasteSegregated_details' }, 
        { id: 'pcm_airQuality', text: 'Air Quality managed?', modelKey: 'env_pcm_airQuality', detailsModelKey: 'env_pcm_airQuality_details' }, 
        { id: 'pcm_waterQuality', text: 'Water Quality managed?', modelKey: 'env_pcm_waterQuality', detailsModelKey: 'env_pcm_waterQuality_details' }, 
        { id: 'pcm_soil', text: 'Soil protection?', modelKey: 'env_pcm_soil', detailsModelKey: 'env_pcm_soil_details' }, 
        { id: 'pcm_noisePollution', text: 'Noise Pollution managed?', modelKey: 'env_pcm_noisePollution', detailsModelKey: 'env_pcm_noisePollution_details' }, 
        { id: 'pcm_emergencyPreparedness', text: 'Emergency Preparedness?', modelKey: 'env_pcm_emergencyPreparedness', detailsModelKey: 'env_pcm_emergencyPreparedness_details' } 
    ] }
  ],
  ssow: [
    { name: 'Compliance', questions: [ 
        { id: 'compliance_ssowPack', text: 'Safe System of Work Pack on Site?', modelKey: 'ssow_compliance_ssowPack', detailsModelKey: 'ssow_compliance_ssowPack_details' }, 
        { id: 'compliance_tbsBriefed', text: 'Task Briefing Sheets (TBS)/Work Instructions Signed?', modelKey: 'ssow_compliance_tbsBriefed', detailsModelKey: 'ssow_compliance_tbsBriefed_details' }, 
        { id: 'compliance_cossBriefing', text: 'COSS Briefing to Work Party?', modelKey: 'ssow_compliance_cossBriefing', detailsModelKey: 'ssow_compliance_cossBriefing_details' }, 
        { id: 'compliance_protectionArrangements', text: 'Protection Arrangements in Place?', modelKey: 'ssow_compliance_protectionArrangements', detailsModelKey: 'ssow_compliance_protectionArrangements_details' }, 
        { id: 'compliance_accessEgressPlan', text: 'Worksite Access/Egress as Plan?', modelKey: 'ssow_compliance_accessEgressPlan', detailsModelKey: 'ssow_compliance_accessEgressPlan_details' }, 
        { id: 'compliance_permits', text: 'Relevant Permits in Place?', modelKey: 'ssow_compliance_permits', detailsModelKey: 'ssow_compliance_permits_details' }, 
        { id: 'compliance_firstAidPtsCheck', text: 'Random First Aid/PTS card checked?', modelKey: 'ssow_compliance_firstAidPtsCheck', detailsModelKey: 'ssow_compliance_firstAidPtsCheck_details' }, 
        { id: 'compliance_patCalibrationChecks', text: 'Pat Test/Calibration Checks?', modelKey: 'ssow_compliance_patCalibrationChecks', detailsModelKey: 'ssow_compliance_patCalibrationChecks_details' }, 
        { id: 'compliance_emergencyDetails', text: 'Emergency Details Available?', modelKey: 'ssow_compliance_emergencyDetails', detailsModelKey: 'ssow_compliance_emergencyDetails_details' }, 
        { id: 'compliance_riskAssessments', text: 'Risk Assessments available?', modelKey: 'ssow_compliance_riskAssessments', detailsModelKey: 'ssow_compliance_riskAssessments_details' }, 
        { id: 'compliance_driverSafety', text: 'Driver Safety/Rest?', modelKey: 'ssow_compliance_driverSafety', detailsModelKey: 'ssow_compliance_driverSafety_details' }, 
        { id: 'compliance_behaviouralCultural', text: 'Safety Awareness?', modelKey: 'ssow_compliance_behaviouralCultural', detailsModelKey: 'ssow_compliance_behaviouralCultural_details' } 
    ] }
  ]
};
</script>

<style scoped>
.StatCard { @apply flex flex-col p-6 rounded-[28px] border border-slate-100 shadow-sm; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1; }
.StatCard .value { @apply text-sm font-black text-[#0A1630] truncate; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>